import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle2, XCircle, Clock, RefreshCw, Phone, Hash } from "lucide-react";
import { toast } from "sonner";
import { fmtUGX } from "@/lib/format";
import { listUssdVerifications, verifyUssdPayment } from "@/lib/momo.functions";

type Verification = {
  id: string;
  external_reference: string;
  msisdn: string;
  network: string;
  amount_ugx: number;
  prn: string;
  status: string;
  admin_note: string | null;
  created_at: string;
};

export function UssdVerificationPanel() {
  const [verifications, setVerifications] = useState<Verification[]>([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState<string | null>(null);
  const [filter, setFilter] = useState<"pending" | "all">("pending");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { verifications: data } = await listUssdVerifications();
      setVerifications(data);
    } catch (e) {
      toast.error("Failed to load pending payments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "pending"
    ? verifications.filter(v => v.status === "pending")
    : verifications;

  async function handleAction(ref: string, approved: boolean) {
    setActioning(ref);
    try {
      const result = await verifyUssdPayment({ external_reference: ref, approved });
      toast.success(approved ? "Payment verified — wallet credited" : "Payment rejected");
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Action failed");
    } finally {
      setActioning(null);
    }
  }

  const pendingCount = verifications.filter(v => v.status === "pending").length;

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-sm">USSD Payment Verifications</h3>
          <p className="text-xs text-muted-foreground">
            {pendingCount > 0 ? `${pendingCount} pending payment${pendingCount > 1 ? "s" : ""} awaiting confirmation` : "No pending payments"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={filter === "pending" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("pending")}
          >
            Pending ({pendingCount})
          </Button>
          <Button
            variant={filter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("all")}
          >
            All
          </Button>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <CheckCircle2 className="h-8 w-8 text-green-600 mb-2" />
          <p className="text-sm text-muted-foreground">No {filter === "pending" ? "pending" : ""} USSD payments to verify</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((v) => (
            <div
              key={v.id}
              className="flex items-center justify-between rounded-lg border p-3"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-semibold">{fmtUGX(v.amount_ugx)}</span>
                  <Badge variant={v.status === "pending" ? "secondary" : v.status === "verified" ? "default" : "destructive"}>
                    {v.status === "pending" && <Clock className="mr-1 h-3 w-3" />}
                    {v.status === "verified" && <CheckCircle2 className="mr-1 h-3 w-3" />}
                    {v.status === "rejected" && <XCircle className="mr-1 h-3 w-3" />}
                    {v.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {v.msisdn}</span>
                  <span className="flex items-center gap-1"><Hash className="h-3 w-3" /> {v.prn}</span>
                  <span>{v.network}</span>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {new Date(v.created_at).toLocaleString()}
                  {v.admin_note && ` · ${v.admin_note}`}
                </div>
              </div>

              {v.status === "pending" && (
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="default"
                    disabled={actioning === v.external_reference}
                    onClick={() => handleAction(v.external_reference, true)}
                  >
                    {actioning === v.external_reference ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={actioning === v.external_reference}
                    onClick={() => handleAction(v.external_reference, false)}
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    Reject
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
